import React, { useEffect, useState } from "react";
import style from "../woundAssessment.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { Button } from "../../../primary";
import AdtIcon from "../../../../static/images/discharge.png";
import LogoImage from "../../../../static/images/measurmenttest.jpg";
import Loader from "../../../../static/images/spinner.svg";
import { Link, useParams } from "react-router-dom";
import { allNullCheck, normalizeValue } from "../../../../utils";
import {
  allWoundClassificationAsync,
  ne1updateassessmentsignlockAsync,
  patientAssessmentAsync,
  setChildAsItsOwnParent,
  setMergeNavigateSuccess,
  setMergeWoundSuccess,
  setTransferNavigateSuccess,
  setTransferWoundSuccess,
  setWoundAssessment,
} from "../../../../app/patientSlice";
import { useWoundInfinitePagination } from "../../../../hooks/useWoundInfinitePagination";
import { ADT_ACTION_MAP, DELETE_PARENT_ASSESSMENT, WOUND_ASSESSMENT_LIMIT } from "../../../../constants";
import { StatusModal } from "../statusModal";
import RevalMergeModal from "./revalMergeModal";
import LockUnlockConfirmationModal from "./lockUnlockConfirmationModal";
import { deleteParentChildAsync, setDeleteAdminStatus } from "../../../../app/appSlice";
import DeleteConfirmationModal from "./deleteConfirmationModal";
import AssessmentThreeDotMenu from "./assessmentThreeDotMenu";
import { useAdminView } from "../../../../hooks/useAdminView";
import { getHistoryCauseText } from "../../../../utils/getHistoryCauseText";
import WoundAssessmentCard from "./woundAssessmentCard";

const getAdtName = (status) => ADT_ACTION_MAP[status] ?? "Admit";

const statusBadg = {
  pendingassessment: <span className={`pt-2 pb-2 ${style.assess_status} ${style.pending_assess}`}>Pending Assessment</span>,
  reviewed: <span className={`pt-2 pb-2  ${style.assess_status} ${style.reviewed}`}>Reviewed</span>,
  pendingreview: <span className={`pt-2 pb-2 ${style.assess_status} ${style.pending_review}`}>Pending Review</span>,
  signedlocked: <span className={`pt-2 pb-2 ${style.assess_status} ${style.signed_locked}`}>Signed &#38; Locked</span>,
};

const WoundAssessmentList = ({ patientData, modalOpen }) => {
  const dispatch = useDispatch();
  const { patientId } = useParams();
  const isAdminView = useAdminView();

  const woundAssessments = useSelector((state) => state.patient.woundAssessments);
  const facilityIntegrationDetails = useSelector((state) => state.patient.facilityIntegrationDetails);
  const userRole = useSelector((state) => state.user);
  const humanAnatomy = useSelector((state) => state.patient.humanAnatomy);
  const historyCause = useSelector((state) => state.patient.historyCause);
  const allWoundClassificationList = useSelector((state) => state.patient.allWoundClassificationList);
  const mergeNavigateSuccess = useSelector((state) => state.patient.mergeNavigateSuccess);
  const transferNavigateSuccess = useSelector((state) => state.patient.transferNavigateSuccess);
  const childAsItsOwnParentAssessment = useSelector((state) => state.patient.childAsItsOwnParent);
  const detailAssessment = useSelector((state) => state.patient.detailAssessment);
  const deleteAdminStatus = useSelector((state) => state.app.deleteAdminStatus);

  const [adtModal, setAdtModal] = useState(false);
  const [lockUnlockToggleType, setLockUnlockToggleType] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [assessmentId, setAssessmentId] = useState(null);
  const [assessReEvalCount, setAssessReEvalCount] = useState(false);
  const [assessTitleContent, setAssessTitleContent] = useState("");
  const [reEvalMergeConfirm, setReEvalMergeConfirm] = useState(false);
  const [getObject, setGetObject] = useState({});
  const [lockUnlockDone, setLockUnlockDone] = useState(false);
  const [lockUnlockToggle, setLockUnlockToggle] = useState(false);

  const { loading, loadMoreRef, resetPagination } = useWoundInfinitePagination({
    hasMore: woundAssessments?.hasMore,
    limit: WOUND_ASSESSMENT_LIMIT,
    onFetch: (offset) =>
      dispatch(
        patientAssessmentAsync(patientId, {
          limit: WOUND_ASSESSMENT_LIMIT,
          offset,
        })
      ),
  });

  const refreshAssessments = () => {
    dispatch(setWoundAssessment("reset"));
    resetPagination();
  };

  useEffect(() => {
    dispatch(allWoundClassificationAsync());
  }, []);

  useEffect(() => {
    refreshAssessments();
  }, [patientId]);

  const hasGroupedAssessments = !!woundAssessments?.assessments;

  useEffect(() => {
    if (!lockUnlockDone) return;
    refreshAssessments();
    const timer = setTimeout(() => {
      setLockUnlockDone(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [lockUnlockDone]);

  useEffect(() => {
    if (!deleteAdminStatus) return;
    refreshAssessments();
    const timer = setTimeout(() => {
      dispatch(setDeleteAdminStatus(false));
    }, 5000);

    return () => clearTimeout(timer);
  }, [deleteAdminStatus, dispatch]);

  useEffect(() => {
    if (!mergeNavigateSuccess && !transferNavigateSuccess) return;

    const timer = setTimeout(() => {
      if (mergeNavigateSuccess) dispatch(setMergeWoundSuccess(null));
      if (transferNavigateSuccess) dispatch(setTransferWoundSuccess(null));
    }, 10000);

    return () => clearTimeout(timer);
  }, [mergeNavigateSuccess, transferNavigateSuccess, dispatch]);

  useEffect(() => {
    if (!hasGroupedAssessments) return;

    const timer = setTimeout(() => {
      dispatch(setMergeNavigateSuccess(false));
      dispatch(setTransferNavigateSuccess(false));
    }, 1000);

    return () => clearTimeout(timer);
  }, [hasGroupedAssessments, dispatch]);

  useEffect(() => {
    if (!hasGroupedAssessments || !childAsItsOwnParentAssessment) return;

    const timer = setTimeout(() => {
      dispatch(setChildAsItsOwnParent(false));
    }, 2000);

    return () => clearTimeout(timer);
  }, [hasGroupedAssessments, childAsItsOwnParentAssessment, dispatch]);

  const openDeleteConfirmation = (_assessmentId, reEvalValue, assessTitle) => {
    setAssessmentId(_assessmentId);
    setShowDeleteConfirmation(true);
    if (reEvalValue > 0) {
      setAssessReEvalCount(true);
    } else {
      setAssessReEvalCount(false);
    }
    setAssessTitleContent(assessTitle);
  };

  const handleDelete = () => {
    const payload = { patientId, assessmentId };
    dispatch(deleteParentChildAsync(payload, DELETE_PARENT_ASSESSMENT, patientAssessmentAsync)).then(() => {
      setShowDeleteConfirmation(false);
    });
    setAssessmentId(null);
  };

  const handleLockUnlockConfirm = () => {
    let assessmentid = getObject.assessmentid;
    let lockValue = getObject.lockValue;
    let signValue = getObject.signValue;
    changeLockUnlockLockedOne(assessmentid, lockValue, signValue);
  };

  const changeLockUnlockLockedOne = (assessmentid, lockValue, signValue) => {
    let getLockValue = lockValue === 1 ? 0 : 1;
    dispatch(setWoundAssessment("reset"));
    const assessment = { signed: signValue, locked: getLockValue };
    let payload = {
      patientId,
      assessmentId: assessmentid,
      assessment,
    };
    dispatch(ne1updateassessmentsignlockAsync(payload, true)).then(() => {
      setLockUnlockDone(true);
      setLockUnlockToggle(false);
    });
  };

  const changeLockUnlockLockedOnePopup = (assessmentid, lockValue, signValue) => {
    const payloadObj = {
      assessmentid: assessmentid,
      lockValue: lockValue,
      signValue: signValue,
      LUONE: true,
    };
    if (lockValue === 1) {
      setLockUnlockToggleType(false);
    } else {
      setLockUnlockToggleType(true);
    }
    setGetObject(payloadObj);
    setLockUnlockToggle(true);
  };

  return (
    <div style={{ position: "relative" }}>
      {lockUnlockDone && detailAssessment?.locked !== undefined && (
        <div className={`alert alert-success ${style.form_alert}`}>
          {detailAssessment?.locked === 1 ? "Wound has been locked" : "Wound has been unlocked"}
        </div>
      )}
      {deleteAdminStatus && <div className={`alert alert-success ${style.form_alert}`}>{"Wound deleted successfully"}</div>}
      {woundAssessments?.assessments?.map((data, index) => {
        return (
          <div key={index}>
            <div className={style.wound_assessment_date}>
              <div className="d-flex align-items-center">
                {/* <div>
                  {data.preAdmitDate ? <span>Pre-admit Date: {format(new Date(data.preAdmitDate), "MM/dd/yyyy hh:mm aa")} </span> : null}
                  {data.admissionDate ? <span>Admission Date: {format(new Date(data.admissionDate), "MM/dd/yyyy hh:mm aa")} </span> : ""}
                  {data.dischargeDate ? <span> - Discharge Date: {format(new Date(data.dischargeDate), "MM/dd/yyyy hh:mm aa")} </span> : null}
                </div> */}
                {!isAdminView && index === 0 && facilityIntegrationDetails?.integretiontab?.manualPatientCreation === 1 && (
                  <Button
                    type={"button"}
                    value={
                      <>
                        <img src={AdtIcon} className={`${style.adtIcon} mr-1`} alt="ADT" /> {getAdtName(patientData?.statusInfo?.status)}
                      </>
                    }
                    classes={{ className: `btn ${style.btn_small} ${style.btn_mobile} mx-2` }}
                    onClick={() => setAdtModal(true)}
                  />
                )}
              </div>
            </div>
            <div>
              {data.assessment ? <div key={index} className={`row m-0 ${style.accordion_panel}`} id={"woundAssessmentListTable"}>
                      <div className="col-lg-2 col-md-3 col-sm-3 pl-0">
                        <div className={style.img_parent}>
                          <img src={data.assessment?.picture || LogoImage} alt="wound" className={style.wound_img} />
                          {!isAdminView && data.assessment?.woundStage && (
                            <span className={`${style.img_stage} pt-1 pb-1`}>{data?.assessment?.woundStage}</span>
                          )}
                        </div>
                      </div>

                      <div className={`col-10`}>
                        <div className={`row align-items-start`}>
                          <div className="col-lg-9 col-md-7 col-sm-6 pl-2">
                            <div className={`${style.panel_heading} ${style.body_heading} mb-4`}>
                              {userRole.role !== "" ? (
                                <Link
                                  params={{ testvalue: "hello" }}
                                  to={{
                                    pathname: "/patients/" + patientId + "/assessment/" + data?.assessment?.id,
                                    state: { assessment: data.assessment, humanAnatomy: humanAnatomy, customlabels: "Passing" },
                                    assessmentsIndex: index,
                                  }}
                                >
                                  <div className="mr-3">{data?.assessment?.title}</div>
                                </Link>
                              ) : (
                                <div className="mr-3">{data?.assessment?.title}</div>
                              )}
                            </div>
                            <div className={`${style.detaile}`}>
                              <div className={`${style.assess_content_label} ${style.assess_content} mb-4`}>
                                <span className={style.assess_label}>History/Cause: </span>
                                <span>{getHistoryCauseText(data.assessment, historyCause)}</span>
                              </div>

                              {/* added for Labels */}
                              <div className={`${style.assess_content_label} ${style.assess_content} mb-4`}>
                                <span className={style.assess_label}>Labels: </span>

                                {[normalizeValue(data?.assessment?.injuryType), normalizeValue(data?.assessment?.bodyType)].filter(Boolean)?.length === 0 ? (
                                  <span className={`${style.sub_label_content_None} mr-3`}>
                                    <span>None</span>
                                  </span>
                                ) : (
                                  [normalizeValue(data?.assessment?.injuryType), normalizeValue(data?.assessment?.bodyType)]
                                    .filter(Boolean)
                                    ?.map((label, index) => (
                                      <span key={index} className={`${style.sub_label_content} mr-3`}>
                                        {label}
                                      </span>
                                    ))
                                )}
                              </div>

                              <div className={`${style.assess_content_woundAcq} ${style.assess_content} mb-4`}>
                                <span className={style.assess_label}>Wound Acquired: </span>{" "}
                                {data?.assessment.woundAcquired ? (
                                  <span className={data?.assessment?.woundAcquired === "NON-POA" ? style.NONPOAColor : style.POAColor}>
                                    {data?.assessment.woundAcquired}
                                  </span>
                                ) : (
                                  "NA"
                                )}
                              </div>

                              <div className={`${style.assess_content} mb-4`}>
                                <span className={`${style.assess_label}`}>Classification: </span>
                                <span className={style.classifi_content}>
                                  {" "}
                                  {data?.assessment.classification !== "unknown" && allNullCheck(data?.assessment.classification)
                                    ? allWoundClassificationList?.find((item) => item.key === data?.assessment.classification)?.value || "Unknown"
                                    : "Unknown"}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="col-lg-3 col-md-2">
                            <div className={`row align-items-center ${style.full_height}`}>
                              <div className={`col-lg-10 col-md-12 ${style.closure_ctr}`}>
                                <div className={style.assess_stage}>{statusBadg[data?.assessment?.assessmentStage]}</div>
                                {!isAdminView && (
                                  <div className={style.closure_container}>
                                    <div className={`${style.clousre_text} text-center`}>
                                      % Change <br />
                                      {data?.assessment?.changeInCm ? (
                                        data?.assessment?.changeInCm !== "Unknown" ? (
                                          <span
                                            className={
                                              data?.assessment?.changeInCm !== "0"
                                                ? data?.assessment?.changeInCm < "0"
                                                  ? `${style.closureColor} ${style.closureColor_green} mt-2`
                                                  : `${style.closureColor} ${style.closureColor_red} mt-2`
                                                : `${style.closureColor} ${style.closureColor_grey} mt-2`
                                            }
                                          >
                                            <span style={{ paddingRight: "4px" }}>
                                              <i
                                                className={
                                                  data?.assessment?.changeInCm !== "0" &&
                                                  (data?.assessment?.changeInCm < "0"
                                                    ? `fa-solid fa-down-long ${style.closureColor_green}`
                                                    : `fa-solid fa-up-long ${style.closureColor_red}`)
                                                }
                                              ></i>
                                            </span>
                                            {Number(data?.assessment?.changeInCm?.replace(/^\D+/g, ""))} cm<sup>2</sup>
                                          </span>
                                        ) : (
                                          <span className={`${style.closureColor} ${style.closureColor_grey} mt-2`}>
                                            0 cm<sup>2</sup>
                                          </span>
                                        )
                                      ) : (
                                        "-"
                                      )}
                                      {data?.assessment?.closurePercentage ? (
                                        data?.assessment?.closurePercentage !== "Unknown" ? (
                                          <span
                                            className={
                                              data?.assessment?.changeInCm !== "0"
                                                ? data?.assessment?.changeInCm < "0"
                                                  ? `${style.closureColor} ${style.closureColor_green} ${data?.assessment?.changeInCm !== "0" && `pl-2`}`
                                                  : `${style.closureColor} ${style.closureColor_red} ${data?.assessment?.changeInCm !== "0" && `pl-2`}`
                                                : `${style.closureColor} ${style.closureColor_grey} ${data?.assessment?.changeInCm !== "0" && `pl-2`}`
                                            }
                                          >
                                            {`(`}
                                            {Number(data?.assessment.closurePercentage?.replace(/^\D+/g, ""))} %{`)`}
                                          </span>
                                        ) : (
                                          <span className={`${style.closureColor} ${style.closureColor_grey}`}>{`(Unknown)`}</span>
                                        )
                                      ) : (
                                        "-"
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <AssessmentThreeDotMenu
                                assessment={data?.assessment}
                                patientData={patientData}
                                modalOpen={modalOpen}
                                handleLockUnlock={changeLockUnlockLockedOnePopup}
                                openDeleteConfirmation={openDeleteConfirmation}
                                setReEvalMergeConfirm={setReEvalMergeConfirm}
                                setAssessmentId={setAssessmentId}
                              />
                            </div>
                          </div>
                        </div>
                        <WoundAssessmentCard assessment={data?.assessment} modalOpen={modalOpen} />
                      </div>
                    </div> : <div className={`pt-4 pb-4 text-center ${style.no_data_content}`}>There are no assessments</div>}
              
            </div>

            {showDeleteConfirmation && (
              <DeleteConfirmationModal
                setShowDeleteConfirmation={setShowDeleteConfirmation}
                assessReEvalCount={assessReEvalCount}
                assessTitleContent={assessTitleContent}
                handleDelete={handleDelete}
              />
            )}
          </div>
        );
      })}
      <div ref={loadMoreRef} style={{ height: 1 }} />
      {loading && (
        <div className={`${"loader"} col-12`}>
          <img src={Loader} alt="loading" />
        </div>
      )}
      {!woundAssessments?.hasMore && <div className={`d-flex justify-content-center`}>No more records</div>}
      {adtModal && <StatusModal patientData={patientData} setAdtModal={setAdtModal} />}

      {reEvalMergeConfirm && <RevalMergeModal isModalOpen={modalOpen} setIsModalOpen={setReEvalMergeConfirm} assessmentId={assessmentId} />}

      {lockUnlockToggle && (
        <LockUnlockConfirmationModal setTogglePopup={setLockUnlockToggle} toggleType={lockUnlockToggleType} handleConfirm={handleLockUnlockConfirm} />
      )}
    </div>
  );
};

export default WoundAssessmentList;
